import { useState } from 'react'
import { Group, Stack, Text } from '@mantine/core'
import { faXmark } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { TextInput } from '../Common/TextInput'
import { Textarea } from '../Common/Textarea'
import { Button } from '../Common/Button'
import { ActionIcon } from '../Common/ActionIcon'
import { formatDate } from '../../utils/formatters'

export interface FileNoteFormProps {
  saving: boolean
  // True once this note has been saved: the form becomes its own confirmation rather
  // than closing, so the message lands where the user clicked Save (MSD-736).
  saved: boolean
  // Save failure from the API — shown inline; Save doubles as the retry.
  apiError: string | null
  onSave: (description: string, detail: string) => void
  onClose: () => void
}

// MC1 File Note eform limits (MSD-236): Description is the short required text,
// Details is the longer optional body. 300 is Priti's working figure — to be
// confirmed by Fil.
const DESCRIPTION_MAX = 300
const DETAIL_MAX = 2000

export function FileNoteForm({ saving, saved, apiError, onSave, onClose }: Readonly<FileNoteFormProps>) {
  const [description, setDescription] = useState('')
  const [detail, setDetail] = useState('')
  // Closing with text entered switches to an inline confirmation instead of
  // window.confirm, which is unreliable inside the Outlook add-in iframe.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  // Only surface the shared hook's error after this form attempted a save.
  const [saveAttempted, setSaveAttempted] = useState(false)

  const isDirty = description !== '' || detail !== ''
  // The note date is not an input — MC1 stamps the creation date server-side.
  const createdDate = formatDate(new Date())

  const handleSave = () => {
    setSaveAttempted(true)
    onSave(description.trim(), detail.trim())
  }

  const handleClose = () => {
    if (isDirty) {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  // Checked before the discard prompt so a confirmed save can never be masked by it.
  // Close goes straight to onClose rather than handleClose: the text still held in state
  // is saved work, so asking whether to discard it would be wrong.
  if (saved) {
    return (
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs" c="success.6">
          File note created successfully.
        </Text>
        <Button label="Close" size="xs" variant="outline" onClick={onClose} />
      </Group>
    )
  }

  if (confirmingDiscard) {
    return (
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs">Discard changes?</Text>
        <Group gap="xs" wrap="nowrap">
          <Button label="Keep editing" size="xs" variant="outline" onClick={() => setConfirmingDiscard(false)} />
          <Button label="Discard" size="xs" variant="filled" color="red" onClick={onClose} />
        </Group>
      </Group>
    )
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fw={600} fz="sm">
          Add File Note
        </Text>
        <Group gap="xs" wrap="nowrap" align="center">
          <Text fz="xs" c="dimmed">
            Created: {createdDate}
          </Text>
          <ActionIcon
            icon={faXmark}
            variant="subtle"
            size="sm"
            aria-label="Close file note form"
            onClick={handleClose}
          />
        </Group>
      </Group>
      <TextInput
        label="Description"
        size="xs"
        withAsterisk
        maxLength={DESCRIPTION_MAX}
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
      />
      <Stack gap={2}>
        <Textarea
          label="Details"
          size="xs"
          maxLength={DETAIL_MAX}
          autosize
          minRows={5}
          value={detail}
          onChange={(event) => setDetail(event.currentTarget.value)}
        />
        <Text fz="xs" c="dimmed" ta="right">
          {detail.length}/{DETAIL_MAX}
        </Text>
      </Stack>
      {saveAttempted && apiError && (
        <Text fz="xs" c="red">
          {apiError}
        </Text>
      )}
      <Group justify="flex-end" gap="xs" wrap="nowrap">
        <Button label="Cancel" size="xs" variant="outline" onClick={handleClose} />
        <Button
          label="Save"
          size="xs"
          variant="filled"
          loading={saving}
          disabled={!description.trim()}
          onClick={handleSave}
        />
      </Group>
    </Stack>
  )
}
